import React, { useState, useEffect, useMemo } from "react";
import { useAuth } from "../../../context/AuthContext";
import { oneKlicks } from "../../../SDK/oneclick";
import { toast } from "react-toastify";
import { Form, InputGroup, Badge } from "react-bootstrap";
import { Button, Select } from "antd";
import { EyeOutlined, DownloadOutlined } from "@ant-design/icons";

const toNumericOrNull = (value) => {
    if (value === undefined || value === null || value === "") {
        return null;
    }
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : null;
};

const recordMatchesInsuranceUser = (record, userData) => {
    if (!record || !userData?.id) {
        return false;
    }

    const userId = toNumericOrNull(userData.id);
    const companyId = toNumericOrNull(userData.company_id || userData.companyId || userData.company);
    const agentId = toNumericOrNull(userData.agent_id || userData.agentId);

    const recordCompanyId = toNumericOrNull(record.company_id || record.companyId);
    const recordAgentId = toNumericOrNull(record.agent_id || record.agentId);

    if (companyId && recordCompanyId && companyId === recordCompanyId) {
        return true;
    }

    if (agentId && recordAgentId && agentId === recordAgentId) {
        return true;
    }

    const directUserCandidates = [
        record.user_id,
        record.userId,
        record.created_by,
        record.createdBy,
        record.owner_id,
        record.ownerId,
        record.customer_id,
        record.customerId,
    ]
        .map(toNumericOrNull)
        .filter((value) => value !== null);

    if (directUserCandidates.includes(userId)) {
        return true;
    }

    const meta = typeof record.meta === "string" ? (() => {
        try {
            return JSON.parse(record.meta);
        } catch (error) {
            return {};
        }
    })() : record.meta || {};

    const metaCandidates = [
        meta?.created_by,
        meta?.user_id,
        meta?.owner_id,
        meta?.agent_id,
    ]
        .map(toNumericOrNull)
        .filter((value) => value !== null);

    return metaCandidates.includes(userId);
};

const Payments = () => {
    const { userData } = useAuth();
    const [payments, setPayments] = useState([]);
    const [upcomingDues, setUpcomingDues] = useState([]);
    const [loading, setLoading] = useState(false);
    const [duesLoading, setDuesLoading] = useState(false);
    const [filter, setFilter] = useState("all"); // all, completed, pending, failed
    const [paymentMethodFilter, setPaymentMethodFilter] = useState("all"); // all, online, cash, cheque, bank_transfer, etc.
    const [searchText, setSearchText] = useState("");
    const companyId = useMemo(
        () => userData?.company_id || userData?.companyId || userData?.company || null,
        [userData]
    );

    const agentId = useMemo(
        () => userData?.agent_id || userData?.agentId || null,
        [userData]
    );

    const extractRows = (response) => {
        if (!response) {
            return [];
        }

        if (response.success === false) {
            return [];
        }

        // Handle direct array in data or nested items
        const data = response.data;
        if (Array.isArray(data)) {
            return data;
        }
        if (data && Array.isArray(data.items)) {
            return data.items;
        }

        // Look for any array property
        if (data && typeof data === 'object') {
            const keys = Object.keys(data);
            for (const key of keys) {
                if (Array.isArray(data[key])) {
                    return data[key];
                }
            }
        }

        return [];
    };

    /**
     * Resilient fetch: tries multiple columns and relational paths (via policies)
     * to ensure data is found regardless of schema used by different tables.
     */
    const multiMatchFetch = async (tableName, userId, customerIds, orderBy = "created_at DESC") => {
        let results = [];
        const seenIds = new Set();
        const userIdNum = toNumericOrNull(userId);

        const tryFetch = async (conditions) => {
            try {
                const value = Object.values(conditions)[0];
                if (value === null || value === undefined) return;

                console.log(`📡 Querying ${tableName}:`, conditions);
                const response = await oneKlicks.read("", tableName, conditions, orderBy, "", "", "0,200");

                if (response?.success) {
                    const rows = extractRows(response);
                    rows.forEach(row => {
                        const rowId = row.id || row.ID || `${row.created_at || row.payment_date || row.due_date}-${row.amount || row.premium_amount || 0}`;
                        if (!seenIds.has(rowId)) {
                            results.push(row);
                            seenIds.add(rowId);
                        }
                    });
                }
            } catch (err) {
                console.warn(`💡 Note: ${tableName} column '${Object.keys(conditions)[0]}' query skipped (likely missing).`);
            }
        };

        // Path A: Relational Fetch (via Policies)
        // This is often the most reliable way for premiums/payments
        try {
            console.log(`� Path A: Resolving policies to fetch from ${tableName}...`);
            let policyConditions = [];
            if (userIdNum) policyConditions.push({ user_id: userIdNum });
            if (customerIds?.length) {
                customerIds.forEach(cid => policyConditions.push({ customer_id: cid }));
            }

            let userPolicies = [];
            for (const cond of policyConditions) {
                const pRes = await oneKlicks.read("", "insurance_policies", cond, "id DESC", "", "", "0,100");
                if (pRes?.success) {
                    const extracted = extractRows(pRes);
                    userPolicies = [...userPolicies, ...extracted];
                }
            }

            // Deduplicate policies by ID
            const policyMap = new Map();
            userPolicies.forEach(p => {
                if (p.id) policyMap.set(p.id, p);
            });

            const uniquePolicyIds = Array.from(policyMap.keys());

            if (uniquePolicyIds.length > 0) {
                console.log(`✅ Found ${uniquePolicyIds.length} user policies. Fetching ${tableName} by policy_id...`);
                for (const pid of uniquePolicyIds) {
                    await tryFetch({ policy_id: pid });
                }
            } else {
                console.log(`ℹ️ No policies found for relational fetch in ${tableName}.`);
            }
        } catch (err) {
            console.warn(`⚠️ Error in relational policy fetch for ${tableName}:`, err.message);
        }

        // Path B: Direct column queries (user_id and customer_id)
        console.log(`🔄 Path B: Direct identifier queries for ${tableName}...`);
        await tryFetch({ user_id: userIdNum });
        if (customerIds?.length) {
            for (const cid of customerIds) {
                await tryFetch({ customer_id: cid });
            }
        }

        // Final fallback (legacy)
        if (userIdNum && (!customerIds || customerIds.length === 0)) {
            await tryFetch({ customer_id: userIdNum });
        }

        return results;
    };

    useEffect(() => {
        const normalizedCompanyId = companyId ? String(companyId) : "";
        fetchPayments(normalizedCompanyId);
        fetchUpcomingDues(normalizedCompanyId);
    }, [companyId, agentId]);

    const getInsuranceCustomerId = async (userId) => {
        try {
            const response = await oneKlicks.read(
                "",
                "insurance_customers",
                { user_id: userId },
                "id DESC",
                "",
                "",
                "0,1"
            );

            if (response?.success) {
                const rows = extractRows(response);
                // Return all IDs found to handle cases with multiple mappings
                return rows.map(r => r.id).filter(Boolean);
            }
            return [];
        } catch (error) {
            console.warn("Error fetching insurance customer ID:", error);
            return null;
        }
    };

    const fetchPayments = async (activeCompanyId) => {
        try {
            setLoading(true);

            const userId = toNumericOrNull(userData?.id);
            // Get company_id/agent_id from activeCompanyId or userData
            const userCompanyId = activeCompanyId || companyId;
            const userAgentId = agentId;
            const normalizedCompanyId = userCompanyId ? String(userCompanyId).trim() : "";

            console.log("==========================================");
            console.log("🔍 FETCHING PAYMENTS");
            console.log("==========================================");
            console.log("📊 User Info:", {
                userId: userId,
                companyId: userCompanyId,
                agentId: userAgentId,
                userDataId: userData?.id
            });

            let paymentRows = [];

            if (userCompanyId) {
                console.log("📤 Fetching payments for company_id:", userCompanyId);
                try {
                    const resp = await oneKlicks.read("", "insurance_payments", { company_id: userCompanyId }, "payment_date DESC, created_at DESC", "", "", "0,200");
                    if (resp?.success) paymentRows = extractRows(resp);
                } catch (e) {
                    console.warn("⚠️ Column company_id likely missing in insurance_payments:", e.message);
                }
            } else if (userAgentId) {
                console.log("📤 Fetching payments for agent_id:", userAgentId);
                try {
                    const resp = await oneKlicks.read("", "insurance_payments", { agent_id: userAgentId }, "payment_date DESC, created_at DESC", "", "", "0,200");
                    if (resp?.success) paymentRows = extractRows(resp);
                } catch (e) {
                    console.warn("⚠️ Column agent_id likely missing in insurance_payments:", e.message);
                }
            } else if (userId) {
                // Fetch by both identifiers as requested by user
                console.log("📤 Fetching payments for user (resilient mode)...");
                const customerIds = await getInsuranceCustomerId(userId);
                paymentRows = await multiMatchFetch("insurance_payments", userId, customerIds, "payment_date DESC, created_at DESC");
            }

            console.log("==========================================");
            console.log("📥 PAYMENT DATA RETRIEVED");
            console.log("==========================================");
            console.log("📊 Raw Rows Count:", paymentRows.length);

            console.log("==========================================");
            console.log("🔄 EXTRACTING PAYMENT ROWS");
            console.log("==========================================");
            console.log("📊 Extracted Rows Count:", paymentRows.length);
            console.log("📋 All Extracted Payments:", paymentRows);

            if (paymentRows.length > 0) {
                console.log("📋 Sample Payment Details:", {
                    id: paymentRows[0].id,
                    customer_id: paymentRows[0].customer_id,
                    company_id: paymentRows[0].company_id,
                    agent_id: paymentRows[0].agent_id,
                    user_id: paymentRows[0].user_id,
                    amount: paymentRows[0].amount,
                    status: paymentRows[0].status
                });
            }

            // Filter payments based on user context
            const beforeFilterCount = paymentRows.length;
            console.log("==========================================");
            console.log("🔍 FILTERING PAYMENTS");
            console.log("==========================================");
            console.log("📊 Before Filter Count:", beforeFilterCount);

            // Simplified filtering - primarily match by company, agent or resolved customer_id
            const resolvedCustomerIds = userId ? await getInsuranceCustomerId(userId) : [];
            console.log("📊 Resolved Customer IDs for Filter:", resolvedCustomerIds);

            paymentRows = paymentRows.filter((payment) => {
                const paymentCustomerId = toNumericOrNull(payment.customer_id);
                const paymentUserId = toNumericOrNull(payment.user_id);
                const paymentCompanyId = toNumericOrNull(payment.company_id);
                const paymentAgentId = toNumericOrNull(payment.agent_id);

                // Priority 1: Match by company_id
                if (userCompanyId) {
                    if (paymentCompanyId === toNumericOrNull(userCompanyId)) {
                        return true;
                    }
                }

                // Priority 2: Match by agent_id
                if (userAgentId) {
                    if (paymentAgentId === toNumericOrNull(userAgentId)) {
                        return true;
                    }
                }

                // Priority 3: Match by user_id
                if (userId && paymentUserId === userId) {
                    return true;
                }

                // Priority 4: Match by ANY resolved customer_id
                if (resolvedCustomerIds.length > 0 && resolvedCustomerIds.includes(paymentCustomerId)) {
                    return true;
                }

                // Priority 5: Final fallback - check if user ID itself matches customer_id (for legacy)
                if (userId && paymentCustomerId === userId) {
                    return true;
                }

                // Priority 6: Catch-all for individual users
                // If we are in individual mode (not company/agent) and data was fetched,
                // it likely matched via policy relation in multiMatchFetch, so we keep it.
                if (userId && !userCompanyId && !userAgentId) {
                    console.log(`✅ Keeping record ${payment.id} via individual mode relationship.`);
                    return true;
                }

                return false;
            });

            console.log("📊 After Filter Count:", paymentRows.length);
            console.log("📋 Filtered Payments:", paymentRows);

            // Map payments to add formatted fields
            paymentRows = paymentRows.map((payment) => {
                const payer =
                    payment.policy_holder_name ||
                    payment.customer_name ||
                    payment.payer_name ||
                    "—";

                const payee =
                    payment.insurer_name ||
                    payment.payee_name ||
                    userData?.company_name ||
                    "Insurance Company Name";

                return {
                    ...payment,
                    policy_number: payment.policy_number || `POL-${payment.policy_id || "--"}`,
                    payer_name: payer,
                    payee_name: payee,
                    claim_number: payment.claim_number || (payment.claim_id ? `CLM-${payment.claim_id}` : null),
                };
            });

            console.log("==========================================");
            console.log("✨ FINAL PAYMENT DATA");
            console.log("==========================================");
            console.log("📊 Final Count:", paymentRows.length);
            console.log("📋 Final Payments:", JSON.stringify(paymentRows, null, 2));

            // Summary for debugging
            if (paymentRows.length === 0 && beforeFilterCount > 0) {
                console.log("==========================================");
                console.log("⚠️ SUMMARY: All payments were filtered out");
                console.log("==========================================");
                console.log("📊 Total payments fetched:", beforeFilterCount);
                console.log("📊 Payments after filtering:", paymentRows.length);
                console.log("💡 Reason: Payment customer_id/agent_id/company_id doesn't match user's ID/company_id");
                console.log("💡 User ID:", userId);
                console.log("💡 User Company ID:", normalizedCompanyId || "None");
                console.log("💡 If you expect to see payments, check:");
                console.log("   1. Does user have company_id set?");
                console.log("   2. Does payment customer_id match user ID?");
                console.log("   3. Does payment agent_id match user ID?");
                console.log("   4. Does payment company_id match user's company_id?");
            }

            console.log("==========================================");
            console.log("✅ PAYMENT FETCH COMPLETED");
            console.log("==========================================");
            setPayments(paymentRows);
        } catch (error) {
            console.log("==========================================");
            console.log("❌ ERROR FETCHING PAYMENTS");
            console.log("==========================================");
            console.error("💥 Error:", error);
            console.error("💥 Error Message:", error?.message);
            console.error("💥 Error Stack:", error?.stack);
            toast.error("Unable to load payments right now");
            setPayments([]);
        } finally {
            setLoading(false);
            console.log("🏁 Loading state set to false");
        }
    };

    const fetchUpcomingDues = async (activeCompanyId) => {
        try {
            setDuesLoading(true);
            const today = new Date();
            today.setHours(0, 0, 0, 0); // Reset time for accurate comparison

            const userId = toNumericOrNull(userData?.id);
            const userCompanyId = activeCompanyId || companyId;
            const userAgentId = agentId;

            console.log("==========================================");
            console.log("🔍 FETCHING UPCOMING DUES");
            console.log("==========================================");

            let rows = [];

            if (userCompanyId) {
                try {
                    const resp = await oneKlicks.read("", "insurance_premiums", { company_id: userCompanyId }, "due_date ASC", "", "", "0,200");
                    if (resp?.success) rows = extractRows(resp);
                } catch (e) {
                    console.warn("⚠️ column company_id missing in insurance_premiums:", e.message);
                }
            } else if (userAgentId) {
                try {
                    const resp = await oneKlicks.read("", "insurance_premiums", { agent_id: userAgentId }, "due_date ASC", "", "", "0,200");
                    if (resp?.success) rows = extractRows(resp);
                } catch (e) {
                    console.warn("⚠️ column agent_id missing in insurance_premiums:", e.message);
                }
            } else if (userId) {
                console.log("📤 Fetching upcoming dues for user (resilient mode)...");
                const customerIds = await getInsuranceCustomerId(userId);
                rows = await multiMatchFetch("insurance_premiums", userId, customerIds, "due_date ASC");

                // If premiums table yields nothing (or fails), fallback to policies
                if (rows.length === 0) {
                    console.log("🔄 Trying fallback: insurance_policies table");
                    rows = await multiMatchFetch("insurance_policies", userId, customerIds, "created_at DESC");

                    if (rows.length > 0) {
                        // Convert policies to premium-like structure
                        rows = rows
                            .filter(policy => policy.status === 'active')
                            .map(policy => ({
                                id: policy.id,
                                policy_id: policy.id,
                                policy_number: policy.policy_number || `POL-${policy.id}`,
                                amount: policy.premium_amount || policy.monthly_premium || 0,
                                due_date: policy.next_premium_date || policy.renewal_date || null,
                                status: 'pending'
                            }))
                            .filter(premium => premium.due_date);
                    }
                }
            }

            if (rows.length) {
                const upcoming = rows.filter((premium) => {
                    if (!premium?.due_date) {
                        return false;
                    }
                    try {
                        const dueDate = new Date(premium.due_date);
                        dueDate.setHours(0, 0, 0, 0);
                        const isUpcoming = dueDate >= today;
                        const isNotPaid = (premium.status || "").toLowerCase() !== "paid";
                        return isUpcoming && isNotPaid;
                    } catch (dateError) {
                        console.warn("⚠️ Invalid due_date format:", premium.due_date);
                        return false;
                    }
                });
                console.log("✅ Filtered upcoming dues:", upcoming.length);
                setUpcomingDues(upcoming);
            } else {
                console.log("ℹ️ No upcoming dues found");
                setUpcomingDues([]);
            }
        } catch (error) {
            console.log("==========================================");
            console.log("❌ ERROR FETCHING UPCOMING DUES");
            console.log("==========================================");
            console.error("💥 Error:", error);
            console.error("💥 Error Message:", error?.message);
            // Don't show error toast - it's optional data
            setUpcomingDues([]);
        } finally {
            setDuesLoading(false);
            console.log("🏁 Upcoming dues loading state set to false");
        }
    };

    const totalPremiumsPaid = useMemo(
        () => payments.reduce((sum, payment) => sum + (parseFloat(payment.amount) || 0), 0),
        [payments]
    );

    const completedPayments = useMemo(
        () => payments.filter((payment) => (payment.status || "").toLowerCase() === "completed").length,
        [payments]
    );

    const linkedClaims = useMemo(
        () => payments.filter((payment) => Boolean(payment.claim_id || payment.claim_number)).length,
        [payments]
    );

    const formatCurrency = (amount) => {
        if (!amount) {
            return "₹0";
        }
        const numAmount = parseFloat(amount);
        return new Intl.NumberFormat("en-IN", {
            style: "currency",
            currency: "INR",
            maximumFractionDigits: 0,
        }).format(numAmount);
    };

    const uniquePaymentMethods = useMemo(() => {
        const methods = new Set();
        payments.forEach((payment) => {
            if (payment.payment_method) {
                methods.add(payment.payment_method);
            }
        });
        return Array.from(methods).sort();
    }, [payments]);

    const filteredPayments = useMemo(() => {
        let filtered = payments;

        // Apply status filter
        if (filter !== "all") {
            filtered = filtered.filter((payment) => {
                const status = (payment.status || "").toLowerCase();
                if (filter === "completed") return status === "completed";
                if (filter === "pending") return status === "pending" || status === "";
                if (filter === "failed") return status === "failed" || status === "rejected";
                return true;
            });
        }

        // Apply payment method filter
        if (paymentMethodFilter !== "all") {
            filtered = filtered.filter((payment) => {
                const method = (payment.payment_method || "").toLowerCase();
                return method === paymentMethodFilter.toLowerCase();
            });
        }

        // Apply search filter
        if (searchText.trim()) {
            const searchLower = searchText.toLowerCase();
            filtered = filtered.filter((payment) => {
                return (
                    payment.policy_number?.toLowerCase().includes(searchLower) ||
                    payment.payer_name?.toLowerCase().includes(searchLower) ||
                    payment.payee_name?.toLowerCase().includes(searchLower) ||
                    payment.payment_method?.toLowerCase().includes(searchLower) ||
                    payment.claim_number?.toLowerCase().includes(searchLower) ||
                    payment.status?.toLowerCase().includes(searchLower)
                );
            });
        }

        return filtered;
    }, [payments, filter, paymentMethodFilter, searchText]);

    const filteredUpcomingDues = useMemo(() => {
        if (!searchText.trim()) {
            return upcomingDues;
        }
        const searchLower = searchText.toLowerCase();
        return upcomingDues.filter((premium) => {
            return (
                premium.policy_number?.toLowerCase().includes(searchLower) ||
                premium.status?.toLowerCase().includes(searchLower)
            );
        });
    }, [upcomingDues, searchText]);

    return (
        <div className="subbody" style={{ width: '100%', padding: '2% 4%' }}>
            <div className="dc-haslayout dc-dbsectionspace">
                <div className="container-fluid py-3">
                    {/* Payment Management Section */}
                    <div className="card mb-5">
                        <div className="card-body" style={{ padding: '24px 30px' }}>
                            <div className="d-flex align-items-center justify-content-between mb-4">
                                <div className="d-flex align-items-center gap-2">
                                    <h5 className="mb-0 font-weight-bold">Payment Management</h5>
                                    <Badge bg="info">Payments</Badge>
                                    <Badge bg="success" style={{ fontSize: '14px', padding: '4px 12px' }}>
                                        Total: {formatCurrency(totalPremiumsPaid)}
                                    </Badge>
                                </div>
                            </div>
                            <div className="d-flex flex-wrap align-items-center gap-3" style={{ marginBottom: '20px' }}>
                                <div style={{ minWidth: '200px', flex: '0 0 auto' }}>
                                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500', color: '#495057' }}>
                                        All Status
                                    </label>
                                    <Select
                                        value={filter}
                                        onChange={setFilter}
                                        style={{ width: '100%' }}
                                        size="large"
                                        options={[
                                            { value: 'all', label: 'All Status' },
                                            { value: 'completed', label: 'Completed' },
                                            { value: 'pending', label: 'Pending' },
                                            { value: 'failed', label: 'Failed' },
                                        ]}
                                    />
                                </div>
                                <div style={{ minWidth: '200px', flex: '0 0 auto' }}>
                                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500', color: '#495057' }}>
                                        All Methods
                                    </label>
                                    <Select
                                        value={paymentMethodFilter}
                                        onChange={setPaymentMethodFilter}
                                        style={{ width: '100%' }}
                                        size="large"
                                        options={[
                                            { value: 'all', label: 'All Methods' },
                                            ...uniquePaymentMethods.map((method) => ({
                                                value: method.toLowerCase(),
                                                label: method.charAt(0).toUpperCase() + method.slice(1).replace(/_/g, ' ')
                                            }))
                                        ]}
                                    />
                                </div>
                                <div style={{ flex: '1', minWidth: '250px', maxWidth: '400px' }}>
                                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500', color: '#495057' }}>
                                        Search
                                    </label>
                                    <InputGroup>
                                        <Form.Control
                                            placeholder="Search payments..."
                                            value={searchText}
                                            onChange={(e) => setSearchText(e.target.value)}
                                            style={{ height: '40px' }}
                                        />
                                    </InputGroup>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="card">
                        <div className="card-body" style={{ padding: '24px 30px' }}>
                            {loading ? (
                                <div className="text-center p-5">
                                    <div className="spinner-border" role="status">
                                        <span className="sr-only">Loading...</span>
                                    </div>
                                </div>
                            ) : filteredPayments.length > 0 ? (
                                <div className="table-responsive">
                                    <table hover size="sm" className="mb-0 align-middle">
                                        <thead>
                                            <tr>
                                                <th>Date</th>
                                                <th>Policy No.</th>
                                                <th>Cust ID</th>
                                                <th>User ID</th>
                                                <th>Paid By</th>
                                                <th>Received By</th>
                                                <th>Amount</th>
                                                <th>Status</th>
                                                <th style={{ width: '150px' }}>Action</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredPayments.map((payment) => (
                                                <tr key={payment.id || `${payment.policy_number}-${payment.created_at}`}>
                                                    <td data-label="Date">
                                                        {payment.created_at ? new Date(payment.created_at).toLocaleDateString('en-IN', { year: '2-digit', month: 'short', day: 'numeric' }) : "N/A"}
                                                    </td>
                                                    <td data-label="Policy No.">
                                                        <div style={{ fontWeight: '600' }}>{payment.policy_number || "N/A"}</div>
                                                        <div style={{ fontSize: '10px', color: '#999' }}>ID: {payment.policy_id || "--"}</div>
                                                    </td>
                                                    <td data-label="Cust ID">{payment.customer_id || "--"}</td>
                                                    <td data-label="User ID">{payment.user_id || "--"}</td>
                                                    <td data-label="Paid By">{payment.payer_name || "—"}</td>
                                                    <td data-label="Received By">{payment.payee_name || "—"}</td>
                                                    <td data-label="Amount" style={{ fontWeight: '600', color: '#111' }}>{formatCurrency(payment.amount)}</td>
                                                    <td data-label="Status">
                                                        <span className={`badge ${(payment.status || "").toLowerCase() === "completed" ? "badge-success" : (payment.status || "").toLowerCase() === "failed" || (payment.status || "").toLowerCase() === "rejected" ? "badge-danger" : "badge-warning"}`}>
                                                            {payment.status || "Pending"}
                                                        </span>
                                                    </td>
                                                    <td data-label="Action">
                                                        <div style={{ display: 'flex', flexDirection: 'row', gap: '6px', alignItems: 'center', flexWrap: 'nowrap', justifyContent: 'flex-end' }}>
                                                            <Button
                                                                href="#"
                                                                className="dc-update-servicesss fit-content btn-info"
                                                                onClick={(e) => {
                                                                    e.preventDefault();
                                                                    // Handle download invoice
                                                                }}
                                                            >
                                                                <DownloadOutlined style={{ fontSize: '11px', marginRight: '4px' }} />
                                                                Invoice
                                                            </Button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <div className="dc-emptydata-holder">
                                    <div className="dc-emptydata">
                                        <div className="dc-emptydetails dc-empty-booking dc-emptyholder-sm">
                                            <span></span>
                                            <em>No payment history found</em>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Upcoming Dues Section */}
                    {(duesLoading || upcomingDues.length > 0) && (
                        <>
                            <div className="card mb-5">
                                <div className="card-body" style={{ padding: '24px 30px' }}>
                                    <div className="d-flex align-items-center justify-content-between mb-4">
                                        <div className="d-flex align-items-center gap-2">
                                            <h5 className="mb-0 font-weight-bold">Upcoming Premium Dues</h5>
                                            <Badge bg="warning">Dues</Badge>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="card">
                                <div className="card-body" style={{ padding: '24px 30px' }}>
                                    {duesLoading ? (
                                        <div className="text-center p-5">
                                            <div className="spinner-border" role="status">
                                                <span className="sr-only">Loading...</span>
                                            </div>
                                        </div>
                                    ) : filteredUpcomingDues.length > 0 ? (
                                        <div className="table-responsive">
                                            <table hover size="sm" className="mb-0 align-middle">
                                                <thead>
                                                    <tr>
                                                        <th>Policy Number</th>
                                                        <th>Due Date</th>
                                                        <th>Amount</th>
                                                        <th>Status</th>
                                                        <th style={{ width: '150px' }}>Action</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {filteredUpcomingDues.map((premium) => (
                                                        <tr key={premium.id}>
                                                            <td data-label="Policy Number">{premium.policy_number || "N/A"}</td>
                                                            <td data-label="Due Date">
                                                                {premium.due_date ? new Date(premium.due_date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : "N/A"}
                                                            </td>
                                                            <td data-label="Amount">{formatCurrency(premium.amount)}</td>
                                                            <td data-label="Status" style={{ textAlign: 'center' }}>
                                                                <span className={`badge ${(premium.status || "").toLowerCase() === "pending" ? "badge-warning" : "badge-secondary"}`}>
                                                                    {premium.status || "Pending"}
                                                                </span>
                                                            </td>
                                                            <td data-label="Action">
                                                                <div style={{ display: 'flex', flexDirection: 'row', gap: '6px', alignItems: 'center', flexWrap: 'nowrap', justifyContent: 'flex-end' }}>
                                                                    <Button
                                                                        href="#"
                                                                        className="dc-update-servicesss fit-content btn-primary"
                                                                        onClick={(e) => {
                                                                            e.preventDefault();
                                                                            // Handle pay now
                                                                        }}
                                                                    >
                                                                        Pay Now
                                                                    </Button>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    ) : (
                                        <div className="dc-emptydata-holder">
                                            <div className="dc-emptydata">
                                                <div className="dc-emptydetails dc-empty-booking dc-emptyholder-sm">
                                                    <span></span>
                                                    <em>No upcoming dues</em>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Payments;

